import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, MessageSquare, StickyNote, FileText, Printer, Plus, Trash2,
  Smartphone, User, Phone, CheckCircle2, Circle, Send, Stethoscope, Banknote, Mail, Barcode, QrCode, Copy, Pencil,
} from "lucide-react";
import api, { formatError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { STATUS_COLORS, fmtDate, fmtEuro } from "@/lib/status";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function RepairDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { meta, can } = useAuth();
  const [r, setR] = useState(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [sendWa, setSendWa] = useState(true);
  const [note, setNote] = useState("");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0 }]);
  const [discount, setDiscount] = useState(0);
  const [taxRate, setTaxRate] = useState(22);
  const [diag, setDiag] = useState({ problem_found: "", tests: [], technician_notes: "", parts: [] });
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ amount: "", method: "Contanti", kind: "acconto" });
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailType, setEmailType] = useState("receipt");
  const [suggestedParts, setSuggestedParts] = useState([]);
  const [partQuery, setPartQuery] = useState("");
  const [partResults, setPartResults] = useState([]);
  const [quoteHint, setQuoteHint] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadInfo, setUploadInfo] = useState(null);
  const [diagPartQuery, setDiagPartQuery] = useState("");
  const [diagPartResults, setDiagPartResults] = useState([]);
  const [staff, setStaff] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ model: "", imei: "", serial_number: "", problem_description: "" });

  const load = useCallback(() => {
    api.get(`/repairs/${id}`).then(({ data }) => {
      setR(data);
      if (data.diagnosis) setDiag({ problem_found: "", tests: [], technician_notes: "", parts: [], ...data.diagnosis });
    }).catch(() => navigate("/repairs"));
  }, [id, navigate]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  useEffect(() => { api.get("/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!quoteOpen || !r) return;
    const q = partQuery.trim();
    if (q.length < 2) { setPartResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/parts", { params: { store_id: r.store_id, q } });
        setPartResults(data.slice(0, 6));
      } catch { setPartResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [partQuery, quoteOpen, r]);

  useEffect(() => {
    const q = diagPartQuery.trim();
    if (q.length < 2 || !r) { setDiagPartResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/parts", { params: { store_id: r.store_id, q } });
        setDiagPartResults(data.slice(0, 6));
      } catch { setDiagPartResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [diagPartQuery, r]);

  if (!r) return <div className="text-muted-foreground">Caricamento…</div>;

  const changeStatus = async () => {
    try {
      await api.patch(`/repairs/${id}/status`, { new_status: newStatus, note: statusNote, send_whatsapp: sendWa });
      toast.success("Stato aggiornato");
      setStatusOpen(false); setStatusNote(""); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/repairs/${id}/notes`, { text: note });
    setNote(""); toast.success("Nota aggiunta"); load();
  };

  const saveDiag = async () => {
    await api.put(`/repairs/${id}/diagnosis`, diag);
    toast.success("Diagnosi salvata"); load();
  };

  const addDiagPart = (p) => {
    setDiag((d) => {
      const parts = d.parts || [];
      const ex = parts.find((x) => x.part_id === p.id);
      const next = ex
        ? parts.map((x) => x.part_id === p.id ? { ...x, quantity: (Number(x.quantity) || 1) + 1 } : x)
        : [...parts, { part_id: p.id, name: p.name, sale_price: p.sale_price || 0, quantity: 1 }];
      return { ...d, parts: next };
    });
    setDiagPartQuery(""); setDiagPartResults([]);
  };
  const setDiagPartQty = (pid, qty) => setDiag((d) => ({ ...d, parts: (d.parts || []).map((x) => x.part_id === pid ? { ...x, quantity: Math.max(1, Number(qty) || 1) } : x) }));
  const removeDiagPart = (pid) => setDiag((d) => ({ ...d, parts: (d.parts || []).filter((x) => x.part_id !== pid) }));

  const addPayment = async () => {
    try {
      await api.post(`/repairs/${id}/payments`, { ...pay, amount: Number(pay.amount) });
      toast.success("Pagamento registrato"); setPayOpen(false);
      setPay({ amount: "", method: "Contanti", kind: "acconto" }); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const openReceipt = () => window.open(`${API}/repairs/${id}/receipt`, "_blank");

  const sendEmailDoc = async () => {
    try {
      const { data } = await api.post(`/repairs/${id}/email-document`, { type: emailType });
      toast.success(`Email inviata a ${data.to}`); setEmailOpen(false);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const quoteTotal = () => {
    const sub = items.reduce((a, i) => a + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
    const taxable = Math.max(sub - Number(discount || 0), 0);
    return taxable + (taxable * Number(taxRate || 0)) / 100;
  };

  const createQuote = async () => {
    try {
      await api.post(`/repairs/${id}/quotes`, { items, discount: Number(discount), tax_rate: Number(taxRate), valid_days: 7 });
      toast.success("Preventivo creato"); setQuoteOpen(false);
      setItems([{ description: "", quantity: 1, unit_price: 0 }]); setDiscount(0);
      setSuggestedParts([]); setPartQuery(""); setPartResults([]); setQuoteHint(false); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const openQuote = async () => {
    setItems([{ description: "", quantity: 1, unit_price: 0 }]);
    setDiscount(0); setTaxRate(22); setQuoteHint(false); setPartQuery(""); setPartResults([]); setSuggestedParts([]);
    setQuoteOpen(true);
    const diagParts = r.diagnosis?.parts || [];
    if (diagParts.length) {
      setItems(diagParts.map((p) => ({ description: p.name, quantity: Number(p.quantity) || 1, unit_price: p.sale_price || 0, part_id: p.part_id || null })));
      setQuoteHint(true);
    } else {
      try {
        const { data } = await api.get("/repairs/price-suggestion", { params: { brand: r.device.brand, model: r.device.model } });
        if (data.found && data.items?.length) {
          setItems(data.items.map((it) => ({ description: it.description, quantity: it.quantity, unit_price: it.unit_price, part_id: it.part_id || null })));
          setDiscount(data.discount || 0); setTaxRate(data.tax_rate ?? 22); setQuoteHint(true);
        }
      } catch (err) {
        console.debug("No price suggestion available:", err);
      }
    }
    try {
      const { data } = await api.get(`/repairs/${id}/suggested-parts`);
      setSuggestedParts(data.parts || []);
    } catch (err) {
      console.debug("No suggested parts available:", err);
      setSuggestedParts([]);
    }
  };

  const openUpload = async () => {    setUploadOpen(true); setUploadInfo(null);
    try {
      const { data } = await api.post(`/repairs/${id}/upload-token`);
      setUploadInfo(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Impossibile generare il QR");
    }
  };

  const assignRepair = async (val) => {
    const uid = val === "none" ? null : val;
    try { await api.patch(`/repairs/${id}/assign`, { assigned_user_id: uid }); toast.success("Tecnico aggiornato"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const addPartItem = (p) => {    const line = { description: p.name, quantity: 1, unit_price: p.sale_price, part_id: p.id };
    setItems((prev) => (prev.length === 1 && !prev[0].description && !Number(prev[0].unit_price)) ? [line] : [...prev, line]);
    setPartQuery(""); setPartResults([]);
    toast.success("Ricambio aggiunto");
  };

  const sendQuote = async (qid) => {
    await api.post(`/quotes/${qid}/send`); toast.success("Preventivo inviato al cliente"); load();
  };

  const delRepair = async () => {
    if (!window.confirm(`Eliminare definitivamente la riparazione ${r.repair_number}? L'operazione è irreversibile.`)) return;
    try { await api.delete(`/repairs/${id}`); toast.success("Riparazione eliminata"); navigate("/repairs"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const dev = r.device;

  const openEdit = () => {
    setEditForm({
      model: dev.model || "",
      imei: dev.imei || "",
      serial_number: dev.serial_number || "",
      problem_description: r.problem_description || "",
    });
    setEditOpen(true);
  };
  const saveDetails = async () => {
    try {
      const { data } = await api.patch(`/repairs/${id}/details`, editForm);
      setR(data);
      setEditOpen(false);
      toast.success("Dettagli aggiornati");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/repairs")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Riparazioni
      </button>

      {/* Header */}
      <Card className={`overflow-hidden border-l-4 ${STATUS_COLORS[r.status_code]?.split(" ")[2] || "border-l-primary"}`}>
        <div className="flex flex-wrap items-start justify-between gap-4 p-6">
          <div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-xl font-extrabold text-primary">{r.repair_number}</span>
              <StatusBadge code={r.status_code} />
            </div>
            <h1 className="mt-1 font-heading text-2xl font-extrabold">{dev.brand} {dev.model}</h1>
            <p className="text-sm text-muted-foreground">{r.customer_name} · {r.store_name}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {can("repairs.change_status") && (
              <Button size="sm" data-testid="change-status-btn" onClick={() => { setNewStatus(r.status_code); setStatusOpen(true); }}>
                Cambia stato
              </Button>
            )}
            {can("quotes.manage") && (
              <Button size="sm" variant="outline" data-testid="new-quote-btn" onClick={openQuote}>
                <FileText className="mr-1 h-4 w-4" /> Preventivo
              </Button>
            )}
            <Button size="sm" variant="outline" data-testid="print-receipt-btn" onClick={openReceipt}>
              <Printer className="mr-1 h-4 w-4" /> Stampa
            </Button>
            <Button size="sm" variant="outline" data-testid="delivery-receipt-btn" onClick={() => window.open(`${API}/repairs/${id}/delivery-receipt`, "_blank")}>
              <Printer className="mr-1 h-4 w-4" /> Ricevuta consegna
            </Button>
            <Button size="sm" variant="outline" data-testid="print-label-btn" onClick={() => window.open(`${API}/repairs/${id}/label`, "_blank")}>
              <Barcode className="mr-1 h-4 w-4" /> Etichetta
            </Button>
            {can("repairs.delete") && (
              <Button size="sm" variant="outline" data-testid="delete-repair-btn" onClick={delRepair}
                className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30">
                <Trash2 className="mr-1 h-4 w-4" /> Elimina
              </Button>
            )}
            <Button size="sm" variant="outline" data-testid="email-document-btn" onClick={() => setEmailOpen(true)}>
              <Mail className="mr-1 h-4 w-4" /> Invia email
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left: info + tabs */}
        <div className="space-y-6 lg:col-span-2">
          <Tabs defaultValue="details">
            <TabsList>
              <TabsTrigger value="details" data-testid="tab-details">Dettagli</TabsTrigger>
              <TabsTrigger value="diagnosis" data-testid="tab-diagnosis">Diagnosi</TabsTrigger>
              <TabsTrigger value="quotes" data-testid="tab-quotes">Preventivi</TabsTrigger>
              <TabsTrigger value="messages" data-testid="tab-messages">WhatsApp</TabsTrigger>
              <TabsTrigger value="payments" data-testid="tab-payments">Pagamenti</TabsTrigger>
              <TabsTrigger value="photos" data-testid="tab-photos">Foto</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 pt-4">
              <Card className="p-5">
                <div className="mb-4 flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Dati riparazione</div>
                  {can("repairs.edit") && (
                    <Button size="sm" variant="outline" data-testid="edit-details-btn" onClick={openEdit}>
                      <Pencil className="mr-1 h-3.5 w-3.5" /> Modifica
                    </Button>
                  )}
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Info icon={User} label="Cliente" value={r.customer_name} />
                  <Info icon={Phone} label="Telefono" value={r.customer_phone} />
                  <Info icon={Smartphone} label="Dispositivo" value={`${dev.brand} ${dev.model}`} />
                  <Info icon={Smartphone} label="IMEI" value={dev.imei || "—"} mono />
                  <Info icon={Smartphone} label="Serial Number" value={dev.serial_number || "—"} mono />
                </div>
                <div className="mt-4 border-t border-border pt-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tecnico assegnato</div>
                  {can("repairs.edit") ? (
                    <Select value={r.assigned_user_id || "none"} onValueChange={assignRepair}>
                      <SelectTrigger className="mt-2 w-full sm:w-72" data-testid="assign-technician"><SelectValue placeholder="Nessun tecnico" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nessun tecnico</SelectItem>
                        {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="mt-1 text-sm" data-testid="assigned-technician-name">{r.assigned_user_name || "—"}</p>
                  )}
                </div>
                <div className="mt-4 border-t border-border pt-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Problema dichiarato</div>
                  <p className="mt-1 text-sm">{r.problem_description}</p>
                </div>
                {r.conditions?.length > 0 && (
                  <div className="mt-4"><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Condizioni</div>
                    <div className="mt-2 flex flex-wrap gap-2">{r.conditions.map((c) => <span key={c} className="rounded-full bg-secondary px-2.5 py-0.5 text-xs">{c}</span>)}</div></div>
                )}
                {r.accessories?.length > 0 && (
                  <div className="mt-3"><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Accessori</div>
                    <div className="mt-2 flex flex-wrap gap-2">{r.accessories.map((c) => <span key={c} className="rounded-full bg-secondary px-2.5 py-0.5 text-xs">{c}</span>)}</div></div>
                )}
              </Card>

              {/* Notes */}
              <Card className="p-5">
                <div className="mb-3 flex items-center gap-2 font-semibold"><StickyNote className="h-4 w-4" /> Note interne</div>
                <div className="space-y-2">
                  {(r.notes || []).map((n) => (
                    <div key={n.id} className="rounded-lg bg-secondary px-3 py-2 text-sm">
                      <p>{n.text}</p>
                      <div className="mt-1 text-xs text-muted-foreground">{n.author} · {fmtDate(n.created_at)}</div>
                    </div>
                  ))}
                  {(!r.notes || r.notes.length === 0) && <p className="text-sm text-muted-foreground">Nessuna nota.</p>}
                </div>
                {can("repairs.edit") && (
                  <div className="mt-3 flex gap-2">
                    <Input data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Aggiungi una nota…" />
                    <Button data-testid="add-note-btn" onClick={addNote}>Aggiungi</Button>
                  </div>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="diagnosis" className="pt-4">
              <Card className="p-5 space-y-4">
                <div className="flex items-center gap-2 font-semibold"><Stethoscope className="h-4 w-4" /> Diagnosi tecnica</div>
                <div className="space-y-2"><Label>Problema riscontrato</Label>
                  <Input data-testid="diag-problem" value={diag.problem_found} onChange={(e) => setDiag({ ...diag, problem_found: e.target.value })} disabled={!can("repairs.edit")} /></div>
                <div className="space-y-2"><Label>Note tecnico</Label>
                  <Textarea value={diag.technician_notes} onChange={(e) => setDiag({ ...diag, technician_notes: e.target.value })} disabled={!can("repairs.edit")} /></div>
                <div className="space-y-2">
                  <Label>Ricambi necessari</Label>
                  {can("repairs.edit") && (
                    <div className="relative">
                      <Input data-testid="diag-part-search" value={diagPartQuery} onChange={(e) => setDiagPartQuery(e.target.value)}
                        placeholder="Cerca ricambio per SKU, nome, marca, compatibilità…" />
                      {diagPartResults.length > 0 && (
                        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-border bg-popover shadow-lg">
                          {diagPartResults.map((p) => (
                            <button key={p.id} type="button" data-testid={`diag-part-result-${p.sku}`} onClick={() => addDiagPart(p)}
                              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-secondary">
                              <span>{p.name} <span className="text-muted-foreground">· {p.sku}</span></span>
                              <span className="text-muted-foreground">{fmtEuro(p.sale_price)}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {(diag.parts || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nessun ricambio collegato. I ricambi aggiunti qui precompilano il preventivo.</p>
                  ) : (
                    <div className="space-y-2" data-testid="diag-parts-list">
                      {(diag.parts || []).map((p) => (
                        <div key={p.part_id} data-testid={`diag-part-${p.part_id}`} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                          <span className="flex-1">{p.name}</span>
                          <span className="text-muted-foreground">{fmtEuro(p.sale_price)}</span>
                          <Input type="number" className="w-16" value={p.quantity} disabled={!can("repairs.edit")}
                            onChange={(e) => setDiagPartQty(p.part_id, e.target.value)} />
                          {can("repairs.edit") && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`diag-part-remove-${p.part_id}`} onClick={() => removeDiagPart(p.part_id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {can("repairs.edit") && <Button data-testid="save-diag-btn" onClick={saveDiag}>Salva diagnosi</Button>}
              </Card>
            </TabsContent>

            <TabsContent value="quotes" className="space-y-4 pt-4">
              {(r.quotes || []).length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground">Nessun preventivo.</Card>}
              {(r.quotes || []).map((q) => {
                const v = q.versions[q.current_version - 1];
                return (
                  <Card key={q.id} className="p-5" data-testid={`quote-${q.quote_number}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-mono font-semibold">{q.quote_number}</span>
                        <span className="ml-2 text-xs text-muted-foreground">v{q.current_version} · {q.status}</span>
                      </div>
                      <div className="font-heading text-lg font-bold">{fmtEuro(v.total)}</div>
                    </div>
                    <table className="mt-3 w-full text-sm">
                      <tbody>
                        {v.items.map((it) => (
                          <tr key={it.id} className="border-b border-border/60">
                            <td className="py-1.5">{it.description}</td>
                            <td className="py-1.5 text-right text-muted-foreground">{it.quantity} ×</td>
                            <td className="py-1.5 text-right">{fmtEuro(it.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-2 space-y-0.5 text-right text-sm text-muted-foreground">
                      {v.discount > 0 && <div>Sconto: -{fmtEuro(v.discount)}</div>}
                      <div>IVA {v.tax_rate}%: {fmtEuro(v.tax)}</div>
                      <div className="font-semibold text-foreground">Totale: {fmtEuro(v.total)}</div>
                    </div>
                    {can("quotes.manage") && q.status !== "APPROVED" && q.status !== "REJECTED" && (
                      <Button size="sm" className="mt-3" data-testid={`send-quote-${q.quote_number}`} onClick={() => sendQuote(q.id)}>
                        <Send className="mr-1 h-4 w-4" /> {q.status === "SENT" ? "Reinvia" : "Invia al cliente"}
                      </Button>
                    )}
                  </Card>
                );
              })}
            </TabsContent>

            <TabsContent value="messages" className="space-y-2 pt-4">
              {(r.messages || []).length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground">Nessun messaggio inviato.</Card>}
              {(r.messages || []).map((m) => (
                <Card key={m.id} className="p-4">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" /> {m.template_name}</span>
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">{m.status}</span>
                  </div>
                  <p className="mt-2 text-sm">{m.body}</p>
                  <div className="mt-1 text-xs text-muted-foreground">{m.phone} · {fmtDate(m.sent_at)}</div>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="photos" className="pt-4">
              <Card className="p-5">
                <div className="mb-4 flex items-center justify-between">
                  <div className="font-semibold">Foto dispositivo</div>
                  {can("repairs.edit") && (
                    <Button size="sm" variant="outline" data-testid="upload-qr-btn" onClick={openUpload}>
                      <QrCode className="mr-1 h-4 w-4" /> Carica da telefono
                    </Button>
                  )}
                </div>
                {(r.photos || []).length === 0 ? <p className="text-sm text-muted-foreground">Nessuna foto.</p> : (
                  <div className="flex flex-wrap gap-3">
                    {r.photos.map((p) => <img key={p.id} src={p.url} alt="" className="h-28 w-28 rounded-lg border border-border object-cover" />)}
                  </div>
                )}
                {r.signature && (
                  <div className="mt-4"><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Firma cliente</div>
                    <img src={r.signature} alt="firma" className="mt-2 h-24 rounded-lg border border-border bg-white" /></div>
                )}
              </Card>
            </TabsContent>
            <TabsContent value="payments" className="pt-4">
              <Card className="p-5">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 font-semibold"><Banknote className="h-4 w-4" /> Pagamenti</div>
                  {can("repairs.edit") && <Button size="sm" data-testid="add-payment-btn" onClick={() => setPayOpen(true)}><Plus className="mr-1 h-4 w-4" /> Registra</Button>}
                </div>
                {(() => {
                  const paid = (r.payments || []).reduce((a, p) => a + p.amount, 0);
                  const lq = (r.quotes || [])[(r.quotes || []).length - 1];
                  const tot = lq ? lq.versions[lq.current_version - 1].total : 0;
                  return (
                    <div className="mb-4 grid grid-cols-3 gap-3 text-center">
                      <div className="rounded-lg bg-secondary/50 p-3"><div className="font-heading text-lg font-bold">{fmtEuro(tot)}</div><div className="text-xs text-muted-foreground">Preventivo</div></div>
                      <div className="rounded-lg bg-emerald-50 p-3 dark:bg-emerald-950/30"><div className="font-heading text-lg font-bold text-emerald-600" data-testid="payments-paid">{fmtEuro(paid)}</div><div className="text-xs text-muted-foreground">Pagato</div></div>
                      <div className="rounded-lg bg-amber-50 p-3 dark:bg-amber-950/30"><div className="font-heading text-lg font-bold text-amber-600">{fmtEuro(Math.max(tot - paid, 0))}</div><div className="text-xs text-muted-foreground">Residuo</div></div>
                    </div>
                  );
                })()}
                <div className="divide-y divide-border">
                  {(r.payments || []).map((p) => (
                    <div key={p.id} className="flex items-center gap-3 py-2.5 text-sm">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${p.kind === "saldo" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"}`}>{p.kind}</span>
                      <span className="flex-1">{p.method}</span>
                      <span className="font-semibold">{fmtEuro(p.amount)}</span>
                      <span className="hidden text-xs text-muted-foreground sm:block">{fmtDate(p.created_at)}</span>
                    </div>
                  ))}
                  {(!r.payments || r.payments.length === 0) && <p className="py-4 text-sm text-muted-foreground">Nessun pagamento registrato.</p>}
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right: timeline */}
        <div>
          <Card className="p-5">
            <h3 className="mb-4 font-heading font-bold">Timeline</h3>
            <div className="space-y-0">
              {[...(r.timeline || [])].reverse().map((ev, i, arr) => (
                <div key={ev.id} className="relative flex gap-3 pb-4 last:pb-0">
                  <div className="flex flex-col items-center">
                    {i === 0 ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <Circle className="h-5 w-5 fill-emerald-500 text-emerald-500" />}
                    {i < arr.length - 1 && <div className="w-px flex-1 bg-border" />}
                  </div>
                  <div className="pb-1">
                    <div className="text-sm font-medium">{meta.statuses?.find((s) => s.code === ev.new_status)?.label || ev.new_status}</div>
                    <div className="text-xs text-muted-foreground">{fmtDate(ev.changed_at)} · {ev.changed_by_name}</div>
                    {ev.note && <div className="mt-0.5 text-xs text-muted-foreground italic">{ev.note}</div>}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Edit details dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifica dati riparazione</DialogTitle>
            <DialogDescription>Aggiorna modello, IMEI, seriale o problema dichiarato.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nome modello</Label>
              <Input data-testid="edit-model" value={editForm.model} onChange={(e) => setEditForm({ ...editForm, model: e.target.value })} /></div>
            <div className="space-y-2"><Label>IMEI</Label>
              <Input data-testid="edit-imei" value={editForm.imei} onChange={(e) => setEditForm({ ...editForm, imei: e.target.value })} /></div>
            <div className="space-y-2"><Label>Serial Number</Label>
              <Input data-testid="edit-serial" value={editForm.serial_number} onChange={(e) => setEditForm({ ...editForm, serial_number: e.target.value })} /></div>
            <div className="space-y-2"><Label>Problema dichiarato</Label>
              <Textarea data-testid="edit-problem" value={editForm.problem_description} onChange={(e) => setEditForm({ ...editForm, problem_description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button data-testid="save-details-btn" onClick={saveDetails}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change status dialog */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cambia stato</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.statuses?.map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea placeholder="Nota (opzionale)" value={statusNote} onChange={(e) => setStatusNote(e.target.value)} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={sendWa} onChange={(e) => setSendWa(e.target.checked)} /> Invia notifica WhatsApp (se previsto)
            </label>
          </div>
          <DialogFooter>
            <Button data-testid="confirm-status-btn" onClick={changeStatus}>Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New quote dialog */}
      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo preventivo</DialogTitle>
            <DialogDescription>Aggiungi righe, ricambi e sconti per generare il preventivo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {quoteHint && (
              <div data-testid="quote-price-hint" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
                Prezzo precompilato dall'ultima riparazione simile — modifica o conferma.
              </div>
            )}
            {suggestedParts.length > 0 && (
              <div data-testid="suggested-parts" className="space-y-1">
                <Label className="text-xs">Ricambi suggeriti per il guasto</Label>
                <div className="flex flex-wrap gap-2">
                  {suggestedParts.map((p) => (
                    <button key={p.id} type="button" data-testid={`suggested-part-${p.id}`} onClick={() => addPartItem(p)}
                      className="rounded-full border border-border px-3 py-1 text-xs transition-colors hover:border-primary hover:bg-primary/10">
                      + {p.name} · {fmtEuro(p.sale_price)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="relative">
              <Input data-testid="quote-part-search" placeholder="Cerca ricambio in magazzino…" value={partQuery} onChange={(e) => setPartQuery(e.target.value)} />
              {partResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-border bg-popover shadow-lg">
                  {partResults.map((p) => (
                    <button key={p.id} type="button" data-testid={`part-result-${p.sku}`} onClick={() => addPartItem(p)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-secondary">
                      <span>{p.name}</span><span className="text-muted-foreground">{fmtEuro(p.sale_price)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {items.map((it, i) => (
              <div key={i} className="flex gap-2">
                <Input placeholder="Descrizione" data-testid={`quote-item-desc-${i}`} value={it.description}
                  onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} />
                <Input type="number" className="w-16" value={it.quantity}
                  onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))} />
                <Input type="number" className="w-24" placeholder="€" value={it.unit_price}
                  onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x))} />
                <Button variant="ghost" size="icon" onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" data-testid="add-quote-item" onClick={() => setItems([...items, { description: "", quantity: 1, unit_price: 0 }])}>
              <Plus className="mr-1 h-4 w-4" /> Riga
            </Button>
            <div className="flex gap-3">
              <div className="flex-1"><Label className="text-xs">Sconto €</Label><Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} /></div>
              <div className="flex-1"><Label className="text-xs">IVA %</Label><Input type="number" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} /></div>
            </div>
            <div className="text-right font-heading text-lg font-bold">Totale: {fmtEuro(quoteTotal())}</div>
          </div>
          <DialogFooter>
            <Button data-testid="save-quote-btn" onClick={createQuote}>Crea preventivo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registra pagamento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Importo €</Label>
              <Input type="number" data-testid="payment-amount" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Tipo</Label>
                <Select value={pay.kind} onValueChange={(v) => setPay({ ...pay, kind: v })}>
                  <SelectTrigger data-testid="payment-kind"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="acconto">Acconto</SelectItem><SelectItem value="saldo">Saldo</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1"><Label>Metodo</Label>
                <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                  <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Contanti">Contanti</SelectItem><SelectItem value="Carta">Carta</SelectItem><SelectItem value="Bonifico">Bonifico</SelectItem></SelectContent>
                </Select></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-payment-btn" onClick={addPayment}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email document dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invia documento via email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Il cliente riceverà un'email con un link sicuro per aprire il PDF.</p>
            <Select value={emailType} onValueChange={setEmailType}>
              <SelectTrigger data-testid="email-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="receipt">Scheda riparazione</SelectItem>
                <SelectItem value="delivery">Ricevuta di consegna</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter><Button data-testid="send-email-btn" onClick={sendEmailDoc}>Invia email</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile photo upload QR dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Carica foto da telefono</DialogTitle>
            <DialogDescription>
              Inquadra il QR con la fotocamera del telefono per scattare o caricare le foto del dispositivo. Appariranno automaticamente qui.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            {uploadInfo ? (
              <>
                <img src={uploadInfo.qr} alt="QR caricamento" className="h-56 w-56 rounded-lg border border-border" data-testid="upload-qr-image" />
                <div className="flex w-full items-center gap-2">
                  <Input readOnly value={uploadInfo.url} data-testid="upload-qr-url" className="text-xs" />
                  <Button size="icon" variant="outline" data-testid="upload-qr-copy"
                    onClick={() => { navigator.clipboard?.writeText(uploadInfo.url); toast.success("Link copiato"); }}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex h-56 items-center justify-center text-sm text-muted-foreground">Generazione QR…</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 text-muted-foreground" />
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className={`text-sm font-medium ${mono ? "font-mono" : ""}`}>{value}</div>
      </div>
    </div>
  );
}
